"use client";
import { Button } from "@cmp-src/primitives/button";
import { Textarea } from "@cmp-src/primitives/textarea";
import { cn } from "@gettactic/helpers";
import { Plus } from "lucide-react";
import { useEffect, useRef } from "react";
import { DescriptionSectionProps } from "./types";
import { classes } from "./utils";

export const DescriptionSection = ({
  request,
  isEditing,
  editedDescription,
  onDescriptionChange,
  onStartEdit,
  onSave,
  onCancel,
  isLoading,
  showTopDivider = true
}: DescriptionSectionProps) => {
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Editability is resolved by the backend per-request (it accounts for
  // category membership, not just org-wide admin permissions).
  const canEdit = Boolean(request.capabilities?.can_edit);

  useEffect(() => {
    if (isEditing && descriptionTextareaRef.current) {
      setTimeout(() => {
        descriptionTextareaRef.current?.focus();
      }, 0);
    }
  }, [isEditing]);

  // Nothing to show for a viewer who can't edit an empty description.
  if (!isEditing && !request.description && !canEdit) {
    return null;
  }

  const startEditOnKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onStartEdit();
    }
  };

  return (
    // Without a divider the heading needs a gap of its own, or it reads as part
    // of the header chips above it. `mt-5` matches the approval panel's offset,
    // so whichever of the two leads the request starts at the same place.
    <section
      className={cn(
        "mb-8",
        showTopDivider
          ? "-mx-8 max-sm:-mx-4 mt-2 border-neutral-100 border-t px-8 pt-6 max-sm:px-4"
          : "mt-5"
      )}
    >
      {isEditing ? (
        <div className="space-y-4">
          <h3 className={cn(classes.sectionHeading, "mb-3")}>Description</h3>
          <Textarea
            ref={descriptionTextareaRef}
            value={editedDescription}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onDescriptionChange(e.target.value)
            }
            className="min-h-[200px] resize-none border-neutral-200 text-neutral-700 leading-relaxed focus:border-neutral-300 focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          />
          <div className="flex space-x-2">
            <Button
              onClick={onSave}
              size="sm"
              className="rounded-brand bg-neutral-900 text-white hover:bg-neutral-800"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </Button>
            <Button
              onClick={onCancel}
              size="sm"
              variant="outline"
              className="rounded-brand border-neutral-200 text-neutral-600 hover:bg-neutral-50"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : request.description ? (
        <>
          <h3 className={cn(classes.sectionHeading, "mb-3")}>Description</h3>
          {/* Click straight into the text to edit (viewers who can edit only). */}
          <div
            {...(canEdit
              ? {
                  role: "button",
                  tabIndex: 0,
                  onClick: onStartEdit,
                  onKeyDown: startEditOnKey
                }
              : {})}
            className={cn(
              "whitespace-pre-wrap font-normal text-neutral-700 text-sm leading-relaxed",
              canEdit &&
                "-mx-2 cursor-text rounded-md px-2 py-1 hover:bg-neutral-50"
            )}
          >
            {request.description}
          </div>
        </>
      ) : (
        // Empty description (editable viewers): a subtle prompt that opens the editor.
        <button
          type="button"
          onClick={onStartEdit}
          className="flex items-center gap-2 font-normal text-neutral-400 text-sm hover:text-neutral-600"
        >
          <Plus className="h-4 w-4" />
          Add a description...
        </button>
      )}
    </section>
  );
};
